import { Label } from "../ui/label";
import { useInstitutions } from "@/lib/tanstack-query/institutions";
import { CreateInstitutionModal } from "./CreateInstitutionModal";
import { useMemo, useState } from "react";
import { Skeleton } from "../ui/skeleton";
import { Input } from "../ui/input";
import { ChevronDown, Search, X } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import type { Institution } from "@/types/institution";
import { cn } from "@/lib/utils";

interface SelectInstitutionProps {
  value: string | null | undefined;
  defaultValue?: string | null | undefined;
  onValueChange: (v: string | null) => void;
  disabled?: boolean;
}

export const SelectInstitution = ({
  value,
  defaultValue,
  onValueChange,
  disabled,
}: SelectInstitutionProps) => {
  const { data: institutions = [], isLoading } =
    useInstitutions(!!defaultValue);
  const [open, setIsOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [changedValue, setChangedValue] = useState(false);

  const selectedInstitution = useMemo(() => {
    const v = changedValue ? value : value || defaultValue;
    return v ? institutions.find((i) => i.id === v) : null;
  }, [value, defaultValue, institutions, changedValue]);

  const handlePopoverOpenChange = (open: boolean) => {
    if (!open) setSearch("");
    setPopoverOpen(open);
  };

  const handleValueChange = (v: Institution | null) => {
    onValueChange(v?.id ?? null);
    setChangedValue(true);
    handlePopoverOpenChange(false);
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Institution</Label>

      {isLoading ? (
        <Skeleton className="w-full h-9" />
      ) : (
        <Popover
          open={popoverOpen}
          onOpenChange={handlePopoverOpenChange}
          modal
        >
          <PopoverTrigger asChild>
            <div className="relative">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={cn(
                  "justify-between w-full font-normal",
                  !selectedInstitution && "text-muted-foreground",
                  disabled && "opacity-50 cursor-not-allowed",
                )}
              >
                <span className="truncate w-0 grow text-left">
                  {selectedInstitution
                    ? `${selectedInstitution.name} (${selectedInstitution.state ?? selectedInstitution.country})`
                    : "Harvard (MA)"}
                </span>
              </Button>
              {selectedInstitution ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1.5 h-6 w-6 hover:bg-primary/5"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleValueChange(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              ) : (
                <ChevronDown className="w-4 h-4 absolute right-2 top-2.5 " />
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="flex flex-col gap-1 p-1 max-h-[50vh] min-w-full w-full"
          >
            <div className="relative">
              <Search className="absolute left-2.5 top-3.5 h-4 w-4 text-muted-foreground" />
              {search && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2.5 top-2 h-6 w-6"
                  onClick={() => setSearch("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search institutions"
              className="w-full p-2 rounded-sm border border-border pl-9"
            />
            <InstitutionList
              search={search}
              onValueChange={handleValueChange}
            />
          </PopoverContent>
        </Popover>
      )}

      <p className="text-muted-foreground text-sm">
        Can't find your institution?{" "}
        <span
          role="button"
          className="text-primary underline cursor-pointer"
          onClick={() => setIsOpen(true)}
        >
          Add it!
        </span>
      </p>

      <CreateInstitutionModal
        open={open}
        onOpenChange={setIsOpen}
        setInstitution={(i) => onValueChange(i.id)}
      />
    </div>
  );
};

const InstitutionList = ({
  search,
  onValueChange,
}: {
  search: string;
  onValueChange: (v: Institution | null) => void;
}) => {
  const { data: institutions = [], isPending } = useInstitutions();
  const filteredInstitutions = search
    ? institutions.filter((i) =>
        i.name.toLowerCase().includes(search.toLowerCase()),
      )
    : institutions;
  return (
    <div className="flex flex-col grow overflow-y-auto max-h-[50vh]">
      {search ? (
        isPending ? (
          Array.from({ length: 10 }).map((_, index) => (
            <Skeleton key={index} className="w-full h-10" />
          ))
        ) : filteredInstitutions.length > 0 ? (
          filteredInstitutions.map((i) => (
            <div
              key={i.id}
              onClick={() => onValueChange(i)}
              className="cursor-pointer text-sm flex gap-2 items-center hover:bg-muted/75 px-2 py-1 rounded-sm"
            >
              {i.name} {i.state ? `(${i.state})` : `(${i.country})`}
            </div>
          ))
        ) : (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No institutions found
          </div>
        )
      ) : (
        <div className="p-6 text-center text-sm text-muted-foreground">
          Start typing to search institutions
        </div>
      )}
    </div>
  );
};
